import React, { useEffect, useState, useMemo } from "react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from "recharts";
import { api, scopeParams } from "../lib/api";
import { useSession } from "../context/SessionContext";
import KpiCard from "../components/KpiCard";
import ModuleHero from "../components/ModuleHero";
import { StatusPill } from "../components/HealthPill";
import SizedChart from "../components/SizedChart";
import ExportButton from "../components/ExportButton";
import { downloadCSV, stamp } from "../lib/csv";
import { fmtInr, fmtInt, monthLabel, shortDate, fmtPct } from "../lib/format";

const Procurement = () => {
  const { session, focusBranchId } = useSession();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("veg");

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api
      .get("/procurement", { params: scopeParams(session, focusBranchId) })
      .then((r) => mounted && setData(r.data))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [session, focusBranchId]);

  const currentCatSeries = useMemo(() => {
    if (!data) return null;
    return data.category_cost_trend?.find((c) => c.category_id === selectedCategory) || data.category_cost_trend?.[0];
  }, [data, selectedCategory]);

  const exportReorderCSV = () => {
    if (!data?.reorder_suggestions) return;
    downloadCSV(
      `angadya-reorder-${stamp()}.csv`,
      [
        { key: "product", label: "Product" },
        { key: "brand", label: "Brand" },
        { get: (r) => (r.is_private_label ? "Y" : ""), label: "Private Label" },
        { key: "variant", label: "Variant" },
        { key: "vendor", label: "Vendor" },
        { key: "current_stock", label: "Current Stock" },
        { key: "avg_daily_velocity", label: "Avg Daily Velocity" },
        { key: "days_of_cover", label: "Days of Cover" },
        { key: "suggested_qty", label: "Suggested Qty" },
        { key: "estimated_cost", label: "Estimated Cost (INR)" },
        { key: "urgency", label: "Urgency" },
        { key: "branch_count", label: "Branch Count" },
      ],
      data.reorder_suggestions
    );
  };

  const exportIndentsCSV = () => {
    if (!data?.pending_indents) return;
    downloadCSV(
      `angadya-indents-${stamp()}.csv`,
      [
        { key: "po_number", label: "PO Number" },
        { key: "status", label: "Status" },
        { key: "vendor", label: "Vendor" },
        { key: "branch_name", label: "Branch" },
        { key: "product", label: "Product" },
        { key: "brand", label: "Brand" },
        { key: "variant", label: "Variant" },
        { key: "qty", label: "Qty" },
        { key: "amount", label: "Amount (INR)" },
        { key: "created_at", label: "Created" },
        { key: "eta_days", label: "ETA (Days)" },
      ],
      data.pending_indents
    );
  };

  return (
    <div className="space-y-6" data-testid="procurement-page">
      {!loading && data?.kpis && (
        <ModuleHero
          testId="procurement-hero"
          eyebrow="Open Commitments"
          title="What you've already committed to buy"
          subtitle="Total value tied up in pending purchase orders — plus the count of urgent reorders that need attention today."
          primary={{
            label: "Open PO value",
            value: data.kpis.open_po_value || 0,
            countFormatter: (n) => fmtInr(n, { compact: true }),
          }}
          secondaries={[
            {
              label: "Open POs",
              value: data.kpis.open_pos,
            },
            {
              label: "Critical reorders",
              value: data.kpis.critical_reorders,
              accent: data.kpis.critical_reorders > 0 ? "red" : "green",
            },
          ]}
          accent={data.kpis.critical_reorders > 0 ? "red" : "violet"}
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <KpiCard testId="pr-kpi-open" label="Open POs" countTarget={loading ? 0 : (data?.kpis.open_pos || 0)} hint="Pending status" />
        <KpiCard testId="pr-kpi-open-value" label="Open PO Value" countTarget={loading ? 0 : (data?.kpis.open_po_value || 0)} countFormatter={(n) => fmtInr(n, { compact: true })} />
        <KpiCard testId="pr-kpi-critical" label="Critical Reorders" countTarget={loading ? 0 : (data?.kpis.critical_reorders || 0)} tone="danger" hint="Days-of-cover < 3" />
      </div>

      {/* Reorder suggestions */}
      <div className="flex items-end justify-between border-b border-ang pb-3 gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-ang-muted">Reorder Suggestions</div>
          <h2 className="font-serif text-2xl mt-1">Per-brand · velocity aware</h2>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-xs text-ang-muted hidden md:inline">Slow-moving brands are not suggested even if numerically low.</span>
          <ExportButton
            label="Export CSV"
            testId="btn-export-csv-reorder"
            onClick={exportReorderCSV}
            disabled={loading || !data?.reorder_suggestions?.length}
          />
        </div>
      </div>
      <div className="ang-panel">
        <table className="w-full text-sm" data-testid="reorder-table">
          <thead>
            <tr className="text-[10px] uppercase tracking-widest text-ang-muted border-b-2 border-[#14142B]">
              <th className="text-left px-4 py-3">Product · Brand</th>
              <th className="text-left px-4 py-3">Variant</th>
              <th className="text-left px-4 py-3">Vendor</th>
              <th className="text-right px-4 py-3 w-24">Stock</th>
              <th className="text-right px-4 py-3 w-28">Velocity/D</th>
              <th className="text-right px-4 py-3 w-28">Days Cover</th>
              <th className="text-right px-4 py-3 w-28">Suggest Qty</th>
              <th className="text-right px-4 py-3 w-32">Est. Cost</th>
              <th className="text-center px-4 py-3 w-28">Urgency</th>
            </tr>
          </thead>
          <tbody>
            {(data?.reorder_suggestions || []).map((s, i) => (
              <tr key={i} className={`border-b border-ang ${i % 2 === 1 ? "bg-[#FAF9F6]/60" : ""}`}>
                <td className="px-4 py-2.5">
                  <div className="font-medium flex items-center gap-2">
                    {s.is_private_label && <span className="w-1 h-3.5 inline-block" style={{ background: "#C9A15C" }} />}
                    {s.product}
                  </div>
                  <div className="text-xs text-ang-muted">{s.brand}</div>
                </td>
                <td className="px-4 py-2.5 text-xs uppercase tracking-widest text-ang-muted">{s.variant}</td>
                <td className="px-4 py-2.5">{s.vendor}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{fmtInt(s.current_stock)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{s.avg_daily_velocity}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{s.days_of_cover}</td>
                <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{fmtInt(s.suggested_qty)}</td>
                <td className="px-4 py-2.5 text-right tabular-nums">{fmtInr(s.estimated_cost, { compact: true })}</td>
                <td className="px-4 py-2.5 text-center">
                  <StatusPill band={s.urgency === "critical" ? "red" : s.urgency === "high" ? "amber" : "green"}>
                    {s.urgency}
                  </StatusPill>
                </td>
              </tr>
            ))}
            {(data?.reorder_suggestions || []).length === 0 && (
              <tr><td colSpan="9" className="px-4 py-8 text-center text-sm text-ang-muted">No reorder suggestions right now.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Vendor trend */}
        <section className="ang-panel lg:col-span-2">
          <header className="border-b border-ang px-6 py-4">
            <div className="text-[10px] uppercase tracking-widest text-ang-muted">Vendor Spend · Last 12 Months</div>
            <h3 className="font-serif text-xl mt-1">Top vendors by spend</h3>
          </header>
          <table className="w-full text-sm" data-testid="vendor-trend-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-ang-muted border-b border-ang">
                <th className="text-left px-6 py-3">Vendor</th>
                <th className="text-left px-6 py-3">Category</th>
                <th className="text-right px-6 py-3 w-32">12M Total</th>
                <th className="text-left px-6 py-3 w-56">Trend</th>
              </tr>
            </thead>
            <tbody>
              {(data?.vendor_trend || []).slice(0, 12).map((v, i) => {
                const max = Math.max(...v.monthly.map((m) => m.amount));
                return (
                  <tr key={v.vendor} className={`border-b border-ang ${i % 2 === 1 ? "bg-[#FAF9F6]/60" : ""}`}>
                    <td className="px-6 py-2.5 font-medium">{v.vendor}</td>
                    <td className="px-6 py-2.5 text-xs text-ang-muted">{v.category}</td>
                    <td className="px-6 py-2.5 text-right tabular-nums">{fmtInr(v.total_last12m, { compact: true })}</td>
                    <td className="px-6 py-2.5">
                      <div className="flex items-end gap-0.5 h-6">
                        {v.monthly.map((m, j) => (
                          <div
                            key={j}
                            title={`${monthLabel(m.month)} · ${fmtInr(m.amount, { compact: true })}`}
                            className="w-1.5"
                            style={{
                              height: `${Math.max(2, (m.amount / max) * 100)}%`,
                              background: j === v.monthly.length - 1 ? "#C9A15C" : "#14142B",
                            }}
                          />
                        ))}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </section>

        {/* Pending indents */}
        <section className="ang-panel">
          <header className="border-b border-ang px-6 py-4 flex items-center justify-between gap-2">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-ang-muted">Pending Indents</div>
              <h3 className="font-serif text-xl mt-1">Purchase orders</h3>
            </div>
            <ExportButton
              label="Export CSV"
              testId="btn-export-csv-indents"
              onClick={exportIndentsCSV}
              disabled={loading || !data?.pending_indents?.length}
            />
          </header>
          <div className="max-h-[420px] overflow-auto" data-testid="indents-list">
            {(data?.pending_indents || []).map((po) => (
              <div key={po.po_number} className="px-6 py-3 border-b border-ang">
                <div className="flex items-baseline justify-between">
                  <div>
                    <div className="text-xs uppercase tracking-widest text-ang-muted">{po.po_number}</div>
                    <div className="font-medium text-sm mt-0.5">{po.vendor}</div>
                  </div>
                  <StatusPill band={po.status === "pending" ? "amber" : po.status === "approved" ? "green" : "green"}>{po.status}</StatusPill>
                </div>
                <div className="flex items-baseline justify-between mt-1.5">
                  <div className="text-xs text-ang-muted">{po.product} · {po.brand} · {po.variant}</div>
                  <div className="text-sm tabular-nums">{fmtInr(po.amount, { compact: true })}</div>
                </div>
                <div className="text-[11px] uppercase tracking-widest text-ang-muted mt-1">
                  {po.branch_name} · ETA {po.eta_days}D · Qty {po.qty}
                </div>
              </div>
            ))}
            {(data?.pending_indents || []).length === 0 && (
              <div className="px-6 py-8 text-center text-sm text-ang-muted">No pending indents.</div>
            )}
          </div>
        </section>
      </div>

      {/* Category cost trend */}
      <section className="ang-panel">
        <header className="flex items-center justify-between border-b border-ang px-6 py-4 flex-wrap gap-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ang-muted">Category Cost Trend · 30D</div>
            <h3 className="font-serif text-xl mt-1">Per-unit procurement cost</h3>
          </div>
          <div className="flex gap-1 flex-wrap" data-testid="cost-category-tabs">
            {(data?.category_cost_trend || []).map((c) => (
              <button
                key={c.category_id}
                data-testid={`cost-cat-${c.category_id}`}
                onClick={() => setSelectedCategory(c.category_id)}
                className={`text-[10px] uppercase tracking-widest px-2 py-1 border transition-colors ${
                  selectedCategory === c.category_id ? "bg-[#14142B] text-white border-[#14142B]" : "bg-white border-ang hover:border-[#14142B]"
                }`}
              >
                {c.category}
              </button>
            ))}
          </div>
        </header>
        <div className="px-6 pt-4 flex items-baseline gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ang-muted">Current unit cost</div>
            <div className="font-serif text-2xl tabular-nums">{currentCatSeries ? `₹${currentCatSeries.current_cost}` : "—"}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ang-muted">30D change</div>
            <div className="font-serif text-2xl tabular-nums" style={{ color: (currentCatSeries?.change_30d_pct || 0) >= 0 ? "#B7402A" : "#2F8B5E" }}>
              {currentCatSeries ? fmtPct(currentCatSeries.change_30d_pct) : "—"}
            </div>
          </div>
        </div>
        <SizedChart height={240} className="px-2 pb-6 pt-4">
          <LineChart data={currentCatSeries?.series || []} margin={{ top: 10, right: 30, left: 10, bottom: 0 }}>
            <CartesianGrid stroke="#E7E4DC" strokeDasharray="3 4" vertical={false} />
            <XAxis dataKey="date" tickFormatter={shortDate} minTickGap={30} axisLine={false} tickLine={false} />
            <YAxis axisLine={false} tickLine={false} width={60} />
            <Tooltip labelFormatter={shortDate} formatter={(v) => [`₹${v}`, "Cost"]} />
            <Line type="monotone" dataKey="cost" stroke="#14142B" strokeWidth={1.6} dot={false} activeDot={{ r: 4, fill: "#C9A15C" }} />
          </LineChart>
        </SizedChart>
      </section>
    </div>
  );
};

export default Procurement;
